import React, { useEffect, useState } from "react";
import { Card, Col, Modal, Row, Spinner } from "react-bootstrap";
import { BiCalendarCheck, BiRupee } from "react-icons/bi";
import { IconContext } from "react-icons";

import { ThemedButton } from "components/BuildingBlocks";
import LoginModal from "components/LoginModal";
import { initiateRazorpay } from "payment/razorpay";
import { useAppContext } from "libs/contextLib";
import axiosObj from "libs/axios";

import { CHECKOUT_URL } from "constants/urls";
import { COMMON_ERROR, LOGGED_OUT_ERROR } from "constants/common";

function SubscriptionPlansList(props) {
  const paidPlans = props.course.subscriptionPlans.paid;
  // TODO: check if there is a trial key
  const trialPlans = props.course.subscriptionPlans.trial;

  return (
    <Row className="mx-auto justify-content-center justify-content-md-start mt-2">
      {trialPlans.map((plan) => (
        <SubscriptionPlan
          key={plan.id}
          plan={plan}
          course={props.course}
          domain={props.domain}
        />
      ))}
      {paidPlans.map((plan) => (
        <SubscriptionPlan
          key={plan.id}
          plan={plan}
          course={props.course}
          domain={props.domain}
        />
      ))}
    </Row>
  );
}

function SubscriptionPlan(props) {
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { authenticatedUser } = useAppContext();
  const isLearnDomain = props.domain === "learn";

  const plan = props.plan;

  function onBuyClick() {
    if (!authenticatedUser) {
      setShowLoginModal(true);
    } else {
      setSelectedPlan(plan);
    }
  }

  return (
    <>
      <CheckoutConfirmation
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        course={props.course}
        plan={props.plan}
      />
      <LoginModal
        showLoginModal={showLoginModal}
        setShowLoginModal={setShowLoginModal}
      />
      <Col xs={8} sm={6} md={4} lg={3} className="mb-4 pl-0">
        <Card bg="light" style={{ maxWidth: "14em", borderRadius: "10px" }}>
          <Card.Header className="text-center">
            <Row className="flex-nowrap justify-content-center align-items-center">
              <Col xs="auto" className="p-0 m-0">
                <IconContext.Provider value={{ size: "1.2em" }}>
                  <BiRupee />
                </IconContext.Provider>
              </Col>
              <Col xs="auto" className="p-0 m-0" style={{ fontSize: "2em" }}>
                {plan.price}
              </Col>
            </Row>
          </Card.Header>
          <Card.Body>
            {plan.attributes.map((attribute) => (
              <Row
                key={attribute.identifier}
                className="flex-nowrap justify-content-center"
              >
                <Col className="offset-2 flex-grow-0 pr-0 mb-1">
                  <IconContext.Provider
                    value={{ size: "2rem", color: "#00bf92" }}
                  >
                    <BiCalendarCheck />
                  </IconContext.Provider>
                </Col>
                <Col className="flex-grow-1 text-nowrap align-self-center">
                  <span className="font-weight-bold">
                    {" " + attribute.value}
                  </span>
                  <span>{" " + attribute.name.split(" ").slice(-1)[0]}</span>
                </Col>
              </Row>
            ))}
            {isLearnDomain ? (
              <Row className="mt-3">
                <Col className="text-center text-nowrap">
                  <ThemedButton
                    disabled={!props.course.allowPlanSelection}
                    onClick={() => onBuyClick(plan)}
                    fontWeight={500}
                  >
                    {plan.price === 0 ? "Sign up for free" : "Select plan"}
                  </ThemedButton>
                </Col>
              </Row>
            ) : null}
          </Card.Body>
        </Card>
      </Col>
    </>
  );
}

function CheckoutConfirmation(props) {
  const course = props.course;
  const plan = props.plan;
  const showModal = props.selectedPlan !== null;
  const { authenticateUser, updateErrorMessage } = useAppContext();
  const [submitting, setSubmitting] = useState();

  async function onConfirmPayClick() {
    setSubmitting(true);
    let checkoutObj;
    try {
      const response = await axiosObj.post(CHECKOUT_URL(""), {
        productVariantId: props.selectedPlan.id,
      });
      checkoutObj = response.data;
    } catch (error) {
      if (
        error.response &&
        error.response.status &&
        error.response.status === 401
      ) {
        authenticateUser(null);
        updateErrorMessage(LOGGED_OUT_ERROR);
      } else {
        updateErrorMessage(COMMON_ERROR);
      }
      setSubmitting(false);
    }
    props.setSelectedPlan(null);
    if (checkoutObj) {
      initiateRazorpay(props.selectedPlan.price * 100, course.id, checkoutObj);
    }
  }

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <>
      {props.selectedPlan ? (
        <Modal
          centered
          show={showModal}
          onHide={() => (submitting ? null : props.setSelectedPlan(null))}
        >
          <Modal.Header closeButton>Order Summary</Modal.Header>
          <Modal.Body>
            <Row>
              <Col>
                <img
                  alt="course cover"
                  src={course.imageUrls[0]}
                  style={{
                    objectFit: "cover",
                    width: "100%",
                    height: "100%",
                  }}
                  className="img-fluid rounded"
                />
              </Col>
              <Col>
                <Col xs={12} className="font-weight-bold">
                  {course.name}
                </Col>
                <Col xs={12} className="text-nowrap text-muted">
                  by {course.teacher.firstName + " " + course.teacher.lastName}
                </Col>
                {plan.attributes.map((attribute) => (
                  <Col key={attribute.identifier} xs={12}>
                    <li>
                      {" " +
                        attribute.value +
                        " " +
                        attribute.name.split(" ").slice(-1)[0]}
                    </li>
                  </Col>
                ))}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Col xs={6}>
              <Col xs={12}>
                Total:
                <span>
                  <IconContext.Provider value={{ size: "1.2rem" }}>
                    <BiRupee />
                  </IconContext.Provider>
                </span>
                {plan.price}
              </Col>
              <Col xs={12} style={{ fontSize: ".5rem" }}>
                *Including price and taxes
              </Col>
            </Col>
            <Col xs={6}>
              <ThemedButton disabled={submitting} onClick={onConfirmPayClick}>
                {submitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                  </>
                ) : null}
                Confirm & Pay
              </ThemedButton>
            </Col>
          </Modal.Footer>
        </Modal>
      ) : null}
    </>
  );
}

export default SubscriptionPlansList;
